/**
 * Where a sentence may be read (D219, D226, D229): the placement rule for
 * the three conditions that read the command in hand rather than the world
 * — `direct_object_is`, `indirect_object_is` and `tool_is` — and their
 * reference check.
 *
 * @remarks
 * A command is in hand in exactly three places: inside a thing's answer to a
 * verb it refuses (`unsupportedVerbNarratives`, D219), inside the arms of a
 * thing's authored verb (`verbInteractions`, D229/E22 — the conditions that
 * choose an arm, the lines it speaks and, D246, the branches among its
 * effects), and inside the conditions of a trigger listening to a command
 * (`on.type` of `command_executed` or `command_rejected`, D226/E19) — and,
 * D246, of the branches among its effects. Anywhere else — a state watcher, a room's
 * description, a thing's inspect lines — nobody typed anything in
 * particular, there is no sentence to read, and a variant keyed on one
 * would be dead on arrival. The loader's own paths are the address, so the
 * rule reads the path it was handed; which triggers listen to a command,
 * the declared-id universe says.
 *
 * @packageDocumentation
 */

import type { DirectObjectIsCondition, IndirectObjectIsCondition, ToolIsCondition } from '@orpheus/content-schema';

import { brokenReference, requireDeclared } from './broken-reference.js';
import { type DeclaredIdSets } from './declared-ids.js';
import { type ReferenceBrokenFailure } from './load-failure.js';

/**
 * Where a command is in hand (D219, D229): inside a thing's
 * `unsupportedVerbNarratives` or its `verbInteractions` — or (D251) a body's
 * `verbInteractions`, or (D252) its `orders` — at any depth. The
 * loader's own paths are the address — every reference walk names its place
 * the same way — so the rule reads the path it was handed rather than asking
 * every caller to say whether it answers a sentence.
 */
const WHERE_A_THING_ANSWERS_A_SENTENCE = /^(?:items\[\d+\]\.(?:unsupportedVerbNarratives|verbInteractions)|actors\[\d+\]\.(?:orders|verbInteractions))\./u;

/**
 * The other place (D226, E19): a trigger's `conditions` — which trigger, the
 * path says; whether it listens to a command, the pack does. D246: and the
 * `when` of a branch among its `effects`, which the engine judges with the
 * same command in hand. (A counter's edge is never such a place, wherever
 * the move that reached it stood: an edge is the amount's, not the sentence's.)
 */
const WHERE_A_TRIGGER_READS = /^triggers\[(?<triggerIndex>\d+)\]\.(?:conditions|effects)/u;

/**
 * Is a command in hand at this path (D219, D226)? Inside a thing's answer to
 * a verb it refuses, or inside the conditions of a trigger that listens to a
 * command. A state watcher's conditions are not such a place: the world
 * changed, nobody typed anything in particular, and there is no sentence to
 * read.
 *
 * @param conditionPath - The condition's path
 * @param declared - The pack's declared-id universe (which triggers listen to a command)
 * @returns Whether a sentence-reading condition may stand here
 */
const isWhereACommandIsInHand = (conditionPath: string, declared: DeclaredIdSets): boolean => {
	if (WHERE_A_THING_ANSWERS_A_SENTENCE.test(conditionPath)) {
		return true;
	}

	const triggerIndexText = WHERE_A_TRIGGER_READS.exec(conditionPath)?.groups?.['triggerIndex'];

	return triggerIndexText !== undefined && declared.commandListeningTriggerIndexes.has(Number(triggerIndexText));
};

/**
 * D219/D226/D229: check one condition that reads the sentence — its thing
 * declared, and itself where a command is in hand.
 *
 * @param condition - The condition
 * @param conditionPath - Its path, for the failure (and the placement rule)
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkSentenceCondition = (
	condition: DirectObjectIsCondition | IndirectObjectIsCondition | ToolIsCondition,
	conditionPath: string,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	if (condition.itemId !== undefined) {
		requireDeclared(declared.itemIds, condition.itemId, `${conditionPath}.itemId`, 'condition names an undeclared item', failures);
	}

	if (!isWhereACommandIsInHand(conditionPath, declared)) {
		failures.push(
			brokenReference(
				conditionPath,
				condition.type,
				`\`${condition.type}\` reads the command being answered, and only a thing's \`unsupportedVerbNarratives\`, a thing's or a body's \`verbInteractions\` or a trigger listening for \`command_executed\` / \`command_rejected\` (its conditions, or a branch among its effects) has one in hand — anywhere else it can never hold`,
			),
		);
	}
};
